
import { defineComponent } from 'vue';

import JumpcloudWordmark from 'client/components/JumpcloudWordmark.vue';
import LoadingIcon from 'client/components/LoadingIcon.vue';
import LoginActionButton from 'client/components/LoginActionButton.vue';
import LoginCard from 'client/components/LoginCard.vue';

import LocalStorageService from 'client/util/LocalStorageService';
import UserLoginStore from 'client/stores/UserLoginStore';
import routerService from 'client/services/routerService';
import userResetPasswordService from 'client/services/userResetPasswordService';

import ProcessPasswordResetMfaError from 'client/containers/user/MfaComponents/mfaUtils/processPasswordResetMfaError';

import { LocalStorageConsts, LoginConsts } from 'client/util/Constants';

const { userPwdResetInfo, userPwdResetMeta } = LocalStorageConsts;
const {
  responseMessages: {
    passwordResetError,
  },
} = LoginConsts;

const EMAIL_RESET_FORM_TEMPLATE = 'resetPasswordUserForm';
const PASSWORD_RESET_SUCCESS_QUERY = 'pwdResetSuccess';

export default defineComponent({
  name: 'DuoCallbackHandler',

  components: {
    JumpcloudWordmark,
    LoadingIcon,
    LoginActionButton,
    LoginCard,
  },

  props: {
    // props from params
    // eslint-disable-next-line vue/prop-name-casing
    duo_code: {
      type: String,
      required: true,
    },
    state: {
      type: String,
      required: true,
    },
  },

  computed: {
    buttonText() {
      return 'Redirecting to Login';
    },

    helpText() {
      return 'We are completing your password reset request.';
    },
  },

  mounted() {
    this.submit();
  },

  methods: {
    async submit(): Promise<void> {
      try {
        const pwdResetInfo = LocalStorageService.getItem(userPwdResetInfo) || '{}';
        const pwdResetMeta = LocalStorageService.getItem(userPwdResetMeta) || '{}';
        const {
          confirm,
          password,
          pwdResetToken,
          requestKey,
        } = JSON.parse(pwdResetInfo);
        const {
          resetTemplate,
          context,
          redirectTo,
        } = JSON.parse(pwdResetMeta);

        const ssoQueryParams = context && redirectTo
          ? { context, redirectTo }
          : {};

        if (resetTemplate === EMAIL_RESET_FORM_TEMPLATE) {
          if (password || confirm) {
            await userResetPasswordService.resetPassword(requestKey, {
              duo_code: this.duo_code,
              confirm,
              password,
              pwdResetToken,
              state: this.state,
            });
            routerService.replaceToUserLogin({
              query: {
                ...ssoQueryParams,
                success: PASSWORD_RESET_SUCCESS_QUERY,
              },
            });
            return;
          }

          await userResetPasswordService.resetPassword(requestKey, {
            duo_code: this.duo_code,
            pwdResetToken,
            state: this.state,
          });

          const store = UserLoginStore();
          store.pwdResetToken = pwdResetToken || '';
          store.pwdResetMfaVerified = true;

          routerService.goToUserPasswordResetForm({
            query: {
              template: EMAIL_RESET_FORM_TEMPLATE,
              key: requestKey,
              ...ssoQueryParams,
            },
          });
          return;
        }

        await userResetPasswordService.resetPassword(requestKey, {
          duo_code: this.duo_code,
          pwdResetToken,
          state: this.state,
        });

        const store = UserLoginStore();
        store.pwdResetToken = pwdResetToken || '';
        store.pwdResetMfaVerified = true;

        routerService.goToUserMfaDirectPasswordReset({
          query: ssoQueryParams,
        });
      } catch (error) {
        const { message = passwordResetError } = ProcessPasswordResetMfaError('duo', error);
        const pwdResetMeta = LocalStorageService.getItem(userPwdResetMeta) || '{}';
        const { context, redirectTo } = JSON.parse(pwdResetMeta);
        const ssoQueryParams = context && redirectTo
          ? { context, redirectTo }
          : {};
        routerService.goToUserLogin({
          params: { error: message },
          query: ssoQueryParams,
        });
      } finally {
        LocalStorageService.removeItem(userPwdResetInfo);
        LocalStorageService.removeItem(userPwdResetMeta);
      }
    },
  },
});
